import React, { useState, useEffect } from "react";
import { Plus, Trash2, Info, ArrowRight, DollarSign, Clock, Package, Briefcase, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { motion, AnimatePresence } from "motion/react";

interface MaterialItem {
  id: string;
  name: string;
  quantity: number;
  unitCost: number;
  total: number;
}

export function EntrepreneurCalculator() {
  const [materials, setMaterials] = useState<MaterialItem[]>([
    { id: "1", name: "", quantity: 1, unitCost: 0, total: 0 }
  ]);
  const [laborHours, setLaborHours] = useState<number>(1);
  const [hourlyRate, setHourlyRate] = useState<number>(0);
  const [fixedCosts, setFixedCosts] = useState<number>(0);
  const [profitMargin, setProfitMargin] = useState<number>(30); // 30% default
  const [platformFee, setPlatformFee] = useState<number>(0); // e.g. EmpreMarket fee or others
  
  const [results, setResults] = useState({
    materialsTotal: 0,
    laborTotal: 0,
    productionCost: 0,
    profitAmount: 0,
    finalPrice: 0,
    breakEven: 0
  });

  useEffect(() => {
    calculate();
  }, [materials, laborHours, hourlyRate, fixedCosts, profitMargin, platformFee]);

  const addMaterial = () => {
    const newId = Math.random().toString(36).substring(2, 9);
    setMaterials([...materials, { id: newId, name: "", quantity: 1, unitCost: 0, total: 0 }]);
  };

  const removeMaterial = (id: string) => {
    if (materials.length > 1) {
      setMaterials(materials.filter(m => m.id !== id));
    }
  };

  const updateMaterial = (id: string, field: keyof MaterialItem, value: any) => {
    setMaterials(materials.map(m => {
      if (m.id === id) {
        const updated = { ...m, [field]: value };
        if (field === "quantity" || field === "unitCost") {
          const q = typeof updated.quantity === 'number' ? updated.quantity : 0;
          const c = typeof updated.unitCost === 'number' ? updated.unitCost : 0;
          updated.total = q * c;
        }
        return updated;
      }
      return m;
    }));
  };

  const calculate = () => {
    const materialsTotal = materials.reduce((acc, m) => acc + (m.total || 0), 0);
    const laborTotal = (laborHours || 0) * (hourlyRate || 0);
    const productionCost = materialsTotal + laborTotal + (fixedCosts || 0);
    
    // Price = Cost / (1 - Margin%)
    const marginMultiplier = 1 + (profitMargin / 100);
    let finalPrice = productionCost * marginMultiplier;
    
    // Add platform fee
    if (platformFee > 0) {
      finalPrice = finalPrice / (1 - (platformFee / 100));
    }

    const profitAmount = finalPrice - productionCost - (finalPrice * (platformFee / 100));

    setResults({
      materialsTotal,
      laborTotal,
      productionCost,
      profitAmount,
      finalPrice,
      breakEven: productionCost
    });
  };

  return (
    <div className="grid gap-6 md:grid-cols-12">
      {/* Input Section */}
      <div className="md:col-span-8 space-y-6">
        {/* Materials Card */}
        <Card className="border-primary/20 shadow-lg overflow-hidden">
          <CardHeader className="bg-primary/5 border-b border-primary/10">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-primary" /> Costos de Materiales
                </CardTitle>
                <CardDescription>Detalla los insumos necesarios para este producto o servicio.</CardDescription>
              </div>
              <Button size="sm" onClick={addMaterial} variant="outline" className="border-primary/30 hover:bg-primary/10 transition-colors">
                <Plus className="h-4 w-4 mr-1" /> Agregar Insumo
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-muted-foreground/20">
              <AnimatePresence mode="popLayout">
                {materials.map((m) => (
                  <motion.div 
                    key={m.id}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className="grid grid-cols-12 gap-3 items-end bg-background p-3 rounded-xl border group hover:border-primary/30 transition-all shadow-sm"
                  >
                    <div className="col-span-12 sm:col-span-4">
                      <Label className="text-[10px] font-bold mb-1 block text-muted-foreground uppercase tracking-wider">Nombre del Insumo</Label>
                      <Input 
                        placeholder="Ej: Papel Bond, Pegamento..." 
                        value={m.name}
                        onChange={(e) => updateMaterial(m.id, "name", e.target.value)}
                        className="h-9 text-sm focus-visible:ring-primary"
                      />
                    </div>
                    <div className="col-span-4 sm:col-span-2">
                      <Label className="text-[10px] font-bold mb-1 block text-muted-foreground uppercase tracking-wider">Cant.</Label>
                      <Input 
                        type="number" 
                        value={m.quantity}
                        onChange={(e) => updateMaterial(m.id, "quantity", e.target.value === "" ? "" : parseFloat(e.target.value))}
                        className="h-9 text-sm focus-visible:ring-primary text-center"
                      />
                    </div>
                    <div className="col-span-4 sm:col-span-3">
                      <Label className="text-[10px] font-bold mb-1 block text-muted-foreground uppercase tracking-wider">Costo Unit.</Label>
                      <div className="relative">
                        <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                        <Input 
                          type="number" 
                          placeholder="0.00"
                          value={m.unitCost}
                          onChange={(e) => updateMaterial(m.id, "unitCost", e.target.value === "" ? "" : parseFloat(e.target.value))}
                          className="pl-8 h-9 text-sm focus-visible:ring-primary"
                        />
                      </div>
                    </div>
                    <div className="col-span-3 sm:col-span-2">
                      <Label className="text-[10px] font-bold mb-1 block text-muted-foreground uppercase tracking-wider">Subtotal</Label>
                      <div className="h-9 flex items-center font-bold text-sm text-primary">
                        ${(m.total || 0).toLocaleString()}
                      </div>
                    </div>
                    <div className="col-span-12 sm:col-span-1 flex justify-end">
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        className="h-8 w-8 text-destructive/60 hover:text-destructive hover:bg-destructive/10 transition-colors"
                        onClick={() => removeMaterial(m.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </CardContent>
          <CardFooter className="bg-primary/5 py-3 flex justify-between items-center border-t border-primary/10">
            <span className="text-sm font-medium text-muted-foreground">Total Insumos:</span>
            <span className="text-lg font-bold text-primary">${results.materialsTotal.toLocaleString()}</span>
          </CardFooter>
        </Card>

        {/* Labor & Overheads Card */}
        <div className="grid gap-6 sm:grid-cols-2">
          <Card className="border-orange-500/20 shadow-md">
            <CardHeader className="pb-3 border-b border-orange-500/10">
              <CardTitle className="flex items-center gap-2 text-orange-600 text-lg">
                <Clock className="h-4 w-4" /> Mano de Obra
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <div className="grid gap-2">
                <Label className="text-xs uppercase font-bold text-muted-foreground">Horas Dedicadas</Label>
                <Input 
                  type="number" 
                  value={laborHours || ""}
                  onChange={(e) => setLaborHours(parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="grid gap-2">
                <Label className="text-xs uppercase font-bold text-muted-foreground">Costo por Hora</Label>
                <div className="relative">
                  <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input 
                    type="number" 
                    className="pl-7"
                    value={hourlyRate || ""}
                    onChange={(e) => setHourlyRate(parseFloat(e.target.value) || 0)}
                  />
                </div>
              </div>
              <div className="pt-2 flex justify-between border-t border-dotted">
                <span className="text-sm text-muted-foreground">Total Tiempo:</span>
                <span className="font-bold text-orange-600">${results.laborTotal.toLocaleString()}</span>
              </div>
            </CardContent>
          </Card>

          <Card className="border-blue-500/20 shadow-md">
            <CardHeader className="pb-3 border-b border-blue-500/10">
              <CardTitle className="flex items-center gap-2 text-blue-600 text-lg">
                <Briefcase className="h-4 w-4" /> Otros Gastos
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <div className="grid gap-2">
                <Label className="text-xs uppercase font-bold text-muted-foreground">Gastos Fijos / Extras</Label>
                <div className="relative">
                  <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input 
                    type="number" 
                    className="pl-7"
                    placeholder="Electricidad, envíos, etc."
                    value={fixedCosts || ""}
                    onChange={(e) => setFixedCosts(parseFloat(e.target.value) || 0)}
                  />
                </div>
                <p className="text-[10px] text-muted-foreground italic">Incluye aquí cualquier gasto adicional por unidad.</p>
              </div>
              <div className="grid gap-2">
                <Label className="text-xs uppercase font-bold text-muted-foreground">Comisión Plataforma (%)</Label>
                <Input 
                  type="number" 
                  value={platformFee || ""}
                  onChange={(e) => setPlatformFee(parseFloat(e.target.value) || 0)}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Summary / Result Section */}
      <div className="md:col-span-4 space-y-6">
        <Card className="bg-slate-900 text-slate-50 border-none shadow-2xl relative overflow-hidden sticky top-20">
          {/* Decorative background elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-primary/20 rounded-full -mr-16 -mt-16 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-orange-500/10 rounded-full -ml-12 -mb-12 blur-2xl" />
          
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" /> Resultados Estimados
            </CardTitle>
            <CardDescription className="text-slate-400">Análisis pro proyectado para tu negocio.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 relative z-10">
            <div className="space-y-2">
              <Label className="text-slate-400 text-xs uppercase tracking-widest font-bold">Ganancia Deseada (%)</Label>
              <div className="flex items-center gap-4">
                <input 
                  type="range" 
                  min="0" 
                  max="500" 
                  step="5"
                  value={profitMargin}
                  onChange={(e) => setProfitMargin(parseInt(e.target.value))}
                  className="flex-1 accent-primary"
                />
                <Badge variant="outline" className="text-primary border-primary bg-primary/10 px-3 py-1 font-mono text-sm min-w-[60px] justify-center">
                  {profitMargin}%
                </Badge>
              </div>
            </div>

            <Separator className="bg-slate-800" />

            <div className="space-y-4">
              <div className="flex justify-between items-center group cursor-help">
                <span className="text-sm text-slate-400 border-b border-dotted border-slate-600 pb-0.5">Costo de Producción</span>
                <span className="font-semibold font-mono">${results.productionCost.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center text-primary">
                <span className="text-sm font-medium">Margen de Ganancia ({profitMargin}%)</span>
                <span className="font-bold font-mono">+${results.profitAmount.toLocaleString()}</span>
              </div>
              {platformFee > 0 && (
                <div className="flex justify-between items-center text-red-400 group cursor-help">
                  <span className="text-sm border-b border-dotted border-red-900 pb-0.5">Tarifa / Comisión ({platformFee}%)</span>
                  <span className="font-semibold font-mono">-${(results.finalPrice * (platformFee/100)).toLocaleString()}</span>
                </div>
              )}
            </div>

            <div className="bg-white/5 rounded-2xl p-6 border border-white/10 mt-6 relative group transform transition-transform hover:scale-[1.02]">
              <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mb-1">Precio Sugerido de Venta</p>
              <div className="text-4xl md:text-5xl font-black text-white metallic-water-text py-2">
                ${results.finalPrice.toLocaleString()}
              </div>
              <p className="text-[10px] text-slate-500 mt-2 flex items-center gap-1 group-hover:text-primary transition-colors">
                <Info className="h-3 w-3" /> Este es un valor sugerido competitivo.
              </p>
            </div>
          </CardContent>
          <CardFooter className="pb-8">
            <Button className="w-full h-12 text-md font-bold gap-2 group" onClick={() => toast.success("¡Estrategia de precios actualizada!")}>
              Aplicar este Precio <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </CardFooter>
        </Card>

        {/* Tip Card */}
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="p-4 flex gap-3 text-sm text-primary/80 leading-relaxed">
            <div className="mt-1">
              <div className="bg-primary/20 p-1.5 rounded-lg border border-primary/30">
                <TrendingUp className="h-3 w-3 text-primary" />
              </div>
            </div>
            <div>
              <p className="font-bold text-primary">Pro Tip Premium</p>
              <p className="text-xs">
                No compitas solo por precio. El valor emocional de tus productos hechos a mano permite márgenes de ganancia superiores al 40%.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
